/**
 * Godkännanden av förberedelseplaner – ETT gränssnitt för vyerna, två vägar:
 *  - Gästprofil: hela operationen i EN lokal lagringstransaktion (AppStore.run → applyPlanCommit).
 *    Fungerar offline. Samma opId två gånger skapar ingenting nytt.
 *  - Kontoprofil: en atomisk, autentiserad serveroperation (POST /api/prep/commit) som validerar mot
 *    serverns aktuella underlag och skriver plan + kalenderpass tillsammans. Kräver kontakt med en
 *    kompatibel server, färdig synk och inga olösta konflikter. Ett förlorat svar hämtas igen med samma
 *    operations-ID; återanslutning godkänner aldrig i sig ett gammalt utkast.
 *
 * Vanliga planändringar utan kalenderpass (mallar, regler, avfärdade förslag, behållna krockar) går
 * via AppStore.run och den vanliga utkorgen/synken i båda profiltyperna.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { applyPlanCommit, PlanStaleError, type PlanCommitRequest, type PlanStaleReason } from '@autoday/core';
import { getStore } from '../store/appStore.ts';
import { getSyncClient } from '../sync/syncClient.ts';
import { tr } from '../i18n/i18n.ts';

/** Varför ett kontogodkännande inte kan skickas just nu. Ingenting har skickats eller skapats. */
export type PlanCommitBlock =
  | 'offline'
  | 'server-unsupported'
  | 'signed-out'
  | 'sync-pending'
  | 'sync-conflicts'
  | 'pending-commit'
  | 'checking';

export type PlanCommitOutcome =
  /** Planen och passen är sparade (gäst: lokalt; konto: på servern och integrerade lokalt). */
  | { ok: true; planId: string; duplicate: boolean; skipped: boolean }
  /** Underlaget har ändrats – inget skapades. Visa ett nytt förslag. */
  | { ok: false; kind: 'stale'; reason: PlanStaleReason; message: string }
  /** Kunde inte skickas (se block) – inget skapades. */
  | { ok: false; kind: 'blocked'; block: PlanCommitBlock; message: string }
  /** Skickat men svaret kom inte fram. Utfallet kontrolleras med samma operations-ID; säg aldrig "inget skapades". */
  | { ok: false; kind: 'unknown'; message: string }
  /** Lokalt lagringsfel (gäst) eller oväntat fel – inget skapades. */
  | { ok: false; kind: 'error'; message: string };

export interface PendingPlanCommit {
  opId: string;
  planId: string;
  sourceKey: string;
  kind: PlanCommitRequest['kind'];
  sentAt: string;
  /** Senaste kontrollförsök (resolve). */
  checkedAt: string | null;
}

export interface PlanCommitReadiness {
  /** true = ett godkännande kan skickas nu (gäst: alltid). */
  ready: boolean;
  block?: PlanCommitBlock;
  /** Begriplig förklaring på aktuellt språk. */
  message?: string;
  pending: PendingPlanCommit | null;
  /** Kontoprofil (servern avgör) eller gäst (lokalt). */
  mode: 'guest' | 'account';
}

export function blockMessage(b: PlanCommitBlock): string {
  switch (b) {
    case 'offline':
      return tr('Du är offline. Förslaget visas, men förberedelserna läggs in först när AutoDay har kontakt med servern.', 'You are offline. The suggestion is shown, but the preparation sessions are only added once AutoDay can reach the server.');
    case 'server-unsupported':
      return tr('Servern stöder inte förberedelseplaner ännu. Allt annat i appen fungerar som vanligt; dina mallar och regler väntar på synk.', 'The server does not support preparation plans yet. Everything else in the app works as usual; your templates and rules are waiting to sync.');
    case 'signed-out':
      return tr('Logga in igen för att lägga in förberedelser i kontot.', 'Sign in again to add preparation sessions to the account.');
    case 'sync-pending':
      return tr('Det finns ändringar som inte synkats ännu. Synka först så att planen bygger på samma underlag som servern.', 'There are changes that have not synced yet. Sync first so the plan is based on the same data as the server.');
    case 'sync-conflicts':
      return tr('Det finns synkkonflikter att lösa först.', 'There are sync conflicts to resolve first.');
    case 'pending-commit':
      return tr('Ett tidigare godkännande väntar på bekräftelse från servern. AutoDay kontrollerar det innan något nytt läggs in.', 'An earlier approval is waiting for confirmation from the server. AutoDay checks it before anything new is added.');
    case 'checking':
      return tr('Kontrollerar anslutningen till servern…', 'Checking the connection to the server…');
  }
}

export function staleMessage(reason: PlanStaleReason): string {
  const m: Record<PlanStaleReason, [string, string]> = {
    'time-passed': ['Tiden har gått – förslaget gäller inte längre. Här är ett nytt förslag.', 'Time has moved on – the suggestion no longer applies. Here is a new suggestion.'],
    'deadline-changed': ['Deadlinens datum eller tid har ändrats. Här är ett nytt förslag.', 'The deadline’s date or time has changed. Here is a new suggestion.'],
    'zone-changed': ['Tidszonen har ändrats. Här är ett nytt förslag.', 'The time zone has changed. Here is a new suggestion.'],
    'source-changed': ['Källposten har ändrats sedan förslaget gjordes. Här är ett nytt förslag.', 'The source item has changed since the suggestion was made. Here is a new suggestion.'],
    'source-gone': ['Källposten finns inte längre eller är dold.', 'The source item no longer exists or is hidden.'],
    'source-review': ['Källposten behöver granskas först.', 'The source item needs to be reviewed first.'],
    'deadline-done': ['Deadlinen är markerad klar här.', 'The deadline is marked done here.'],
    'not-deadline': ['Posten räknas inte längre som deadline.', 'The item no longer counts as a deadline.'],
    recurring: ['Återkommande källdatum stöds inte av förberedelseplaner ännu.', 'Recurring source dates are not supported by preparation plans yet.'],
    'template-changed': ['Mallen har ändrats sedan förslaget gjordes. Här är ett nytt förslag.', 'The template has changed since the suggestion was made. Here is a new suggestion.'],
    'template-deleted': ['Mallen har tagits bort. Välj en annan mall.', 'The template has been deleted. Choose another template.'],
    'calendar-invalid': ['Målkalendern finns inte eller är inte skrivbar.', 'The target calendar does not exist or is not writable.'],
    'plan-changed': ['Planen har ändrats på en annan enhet eller i en annan flik. Här är ett nytt förslag.', 'The plan was changed on another device or in another tab. Here is a new suggestion.'],
    'plan-exists': ['Det finns redan en förberedelseplan för den här deadlinen.', 'There is already a preparation plan for this deadline.'],
    'event-changed': ['Ett pass har ändrats sedan förslaget gjordes. Här är ett nytt förslag.', 'A session has changed since the suggestion was made. Here is a new suggestion.'],
    'slot-taken': ['En av tiderna är inte längre ledig. Här är ett nytt förslag.', 'One of the times is no longer free. Here is a new suggestion.'],
    'singles-exist': ['Det finns redan förberedelser för den här deadlinen. Välj uttryckligen om planen ska läggas till.', 'There is already preparation for this deadline. Choose explicitly whether to add the plan.'],
    invalid: ['Förslaget är ogiltigt. Här är ett nytt förslag.', 'The suggestion is invalid. Here is a new suggestion.'],
  };
  return tr(...m[reason]);
}

/**
 * Genomför ett godkännande (approve/replan/end/import). Returnerar alltid ett ärligt utfall – aldrig ett
 * framgångsbesked utan att planen faktiskt sparats, och aldrig "inget skapades" utan belägg.
 */
export async function runPlanCommit(req: PlanCommitRequest): Promise<PlanCommitOutcome> {
  const store = getStore();
  if (!store.syncEnabled) {
    try {
      let skipped = false;
      let planId = req.kind === 'import' ? req.plan.id : req.planId;
      const r = await store.run((s) => {
        const res = applyPlanCommit(s, req, store.clock.now());
        skipped = res.skipped;
        planId = res.planId;
        return res;
      });
      return { ok: true, planId, duplicate: r.duplicate, skipped };
    } catch (e) {
      if (e instanceof PlanStaleError) return { ok: false, kind: 'stale', reason: e.reason, message: staleMessage(e.reason) };
      return { ok: false, kind: 'error', message: e instanceof Error ? e.message : String(e) };
    }
  }
  return getSyncClient().commitPlan(req);
}

/** Kontrollera ett obekräftat godkännande igen (samma operations-ID). Gäst: inget att kontrollera. */
export async function resolvePendingPlanCommit(): Promise<PlanCommitOutcome | { ok: false; kind: 'not-applied'; message: string } | null> {
  const store = getStore();
  if (!store.syncEnabled) return null;
  return getSyncClient().resolvePlanCommit();
}

const GUEST_READY: PlanCommitReadiness = { ready: true, pending: null, mode: 'guest' };

/** Kan ett godkännande skickas nu? Reaktiv; samma svar i alla vyer. */
export function usePlanCommitReadiness(): PlanCommitReadiness {
  const store = getStore();
  const sync = store.syncEnabled ? getSyncClient() : null;
  // Samma prenumerationsfunktion mellan renderingar (annars prenumererar React om vid varje rendering).
  const subscribe = useCallback(
    (cb: () => void) => {
      const a = store.subscribe(cb);
      const b = sync ? sync.subscribe(cb) : () => undefined;
      return () => {
        a();
        b();
      };
    },
    [store, sync],
  );
  return useSyncExternalStore(subscribe, () => (sync ? sync.planReadiness() : GUEST_READY), () => GUEST_READY);
}
